import configuratorApi from '@/api/configurator-api'
import stepsApi from '@/api/steps-api'
import i18n from '@/libs/i18n'

const emptyOffer = () => ({
  orderId: null,
  items: [],
  boxes: [],
  noBox: [],
  extras: [],
  packagingType: null,
})

const state = {
  step: 1,
  catalog: { curtains: [], control_boxes: [], extras: [] },
  ...emptyOffer(),
  order: null,
  legacy: false,
  focusKey: null,
  warnings: {},
  errors: {},
}

const curtainOf = (state, item) => state.catalog.curtains.find(c => c.product_id === item.product_id && c.serie === item.serie)

const extraRules = (state, productId) => {
  let rules = null
  state.catalog.extras.forEach(category => category.products.forEach(product => {
    if (product.id === productId) rules = { ...product.rules, category: category.ref }
  }))
  return rules || {}
}

// Mismo criterio que el tarificador anterior: super_admin crea en el cliente 1
const currentClientId = () => {
  const userData = JSON.parse(localStorage.getItem('userData'))
  return userData.roles[0].name === 'super_admin' ? 1 : userData.clients[0].id
}

// Pintura de guías / embellecedor: la cortina tiene que llevar ese elemento
const meetsRequirement = (state, rules, key) => {
  if (!rules.requires) return true
  const hasCategory = category => state.extras.some(e => e.curtain === key && extraRules(state, e.product_id).category === category)
  if (rules.requires === 'trim') return hasCategory('embellecedor')
  const item = state.items.find(i => i.key === key)
  return !!(item && item.calc && item.calc.guias) || hasCategory('guias')
}

// Quita las pinturas que se han quedado sin su elemento
const pruneDependents = (state, key) => {
  state.extras = state.extras.filter(e => e.curtain !== key || meetsRequirement(state, extraRules(state, e.product_id), key))
}

// Mismas reglas que el tarificador anterior (steps/Second.vue + libs/veevalidate.js)
const MIN_MEASURE = 500
const measureErrors = item => {
  const useL2 = item.measure === 'L2'
  const l = useL2 ? item.l2 : item.l1
  const h = useL2 ? item.h2 : item.h1
  const maxLength = item.serie ? null : 12000
  const maxHeight = item.serie ? 10000 : 8000
  const outOfRange = (value, max) => !!value && (value < MIN_MEASURE || (max && value > max))
  return {
    length: outOfRange(l, maxLength) ? 'configurator.out_range_error' : null,
    height: outOfRange(h, maxHeight) ? 'configurator.out_range_error' : null,
  }
}

// Errores de la API que el tarificador anterior traducía
const apiError = error => {
  if (error === 'l1 supera 12000' || error === 'h1 supera 8000') {
    return `${i18n.t('configurator.size_max_allowed')} (L1 > 12000 ; H1 > 8000)`
  }
  return error || i18n.t('configurator.out_range_error')
}

// Borrador en localStorage (por usuario) para poder recargar o cambiar de paso sin perder la oferta
const draftKey = () => {
  const userData = JSON.parse(localStorage.getItem('userData')) || {}
  return `configuratorDraft_${userData.id || ''}`
}
const readDraft = () => {
  try {
    return JSON.parse(localStorage.getItem(draftKey()))
  } catch (e) {
    return null
  }
}
export const clearDraft = () => {
  try {
    localStorage.removeItem(draftKey())
  } catch (e) {
    // sin localStorage no hay borrador que borrar
  }
}

const notifyError = (commit, title) => {
  commit('notifications', { title, variant: 'danger' }, { root: true })
  commit('setShowNotifications', true, { root: true })
}

const getters = {
  getStep: state => state.step,
  getCatalog: state => state.catalog,
  getItems: state => state.items,
  getBoxes: state => state.boxes,
  getExtras: state => state.extras,
  getOrder: state => state.order,
  isLegacy: state => state.legacy,
  getWarnings: state => state.warnings,
  getErrors: state => state.errors,
  getPackagingType: state => state.packagingType,
  getDraft: state => ({
    orderId: state.orderId,
    step: state.step,
    items: state.items,
    boxes: state.boxes,
    noBox: state.noBox,
    extras: state.extras,
    packagingType: state.packagingType,
  }),
  getClientId: state => (state.order ? state.order.client.id : currentClientId()),
  getGroups: state => {
    const groups = []
    state.items.forEach(item => {
      let group = groups.find(g => g.id === item.group)
      if (!group) {
        group = {
          id: item.group, key: `C${item.group}`, curtain: curtainOf(state, item), items: [],
        }
        groups.push(group)
      }
      group.items.push(item)
    })
    return groups
  },
  measureErrors: () => item => measureErrors(item),
  isItemComplete: () => item => {
    const hasMeasures = item.measure === 'L2' ? item.l2 && item.h2 : item.l1 && item.h1
    if (!hasMeasures) return false
    const errors = measureErrors(item)
    if (errors.length || errors.height) return false
    // Sin tarifa o SSB (precio de la ficha del cliente): no hay tabla que calcular
    if (item.sin_tarifa || item.price_by) return true
    return !!item.calc && !item.calcError && (!item.control_box || !!item.position)
  },
  // SSB sin €/m² en la ficha del cliente: la pone Tecnitex
  ssbPriceMissing: (state, getters, rootState, rootGetters) => {
    const client = rootGetters['clients/getClient']
    return !(client && client.id === getters.getClientId && Number(client.ssb_price) > 0)
  },
  ssbKitPriceMissing: (state, getters, rootState, rootGetters) => {
    const client = rootGetters['clients/getClient']
    return !(client && client.id === getters.getClientId && Number(client.ssb_kit_price) > 0)
  },
  boxOfItem: state => key => state.boxes.find(b => b.curtains.includes(key)),
  getNoBox: state => state.noBox,
  isNoBox: state => key => state.noBox.includes(key),
  extrasOfItem: state => key => state.extras.filter(e => e.curtain === key),
  requiresSig: state => key => state.extras.some(e => e.curtain === key && extraRules(state, e.product_id).requires_sig),
  extraAppliesTo: state => (product, item) => (product.rules.applies_to || []).includes(item.productRef)
    && meetsRequirement(state, product.rules, item.key),
}

const mutations = {
  setStep(state, val) {
    state.step = val
  },
  setCatalog(state, val) {
    state.catalog = val
  },
  setOrder(state, val) {
    state.order = val
    state.orderId = val ? val.id : null
  },
  // Cortina que hay que abrir al llegar al paso 2 (desde un error del resumen)
  setFocus(state, key) {
    state.focusKey = key
  },
  setLegacy(state, val) {
    state.legacy = val
  },
  setWarnings(state, val) {
    state.warnings = val || {}
  },
  setErrors(state, val) {
    state.errors = val || {}
  },
  setPackagingType(state, val) {
    state.packagingType = val
  },
  restoreOffer(state, offer) {
    Object.assign(state, emptyOffer(), offer, { noBox: offer.noBox || [] })
  },
  resetOffer(state) {
    Object.assign(state, emptyOffer(), {
      step: 1, order: null, legacy: false, warnings: {}, errors: {},
    })
  },
  addCurtains(state, { curtain, quantity }) {
    let group = state.items.find(i => i.product_id === curtain.product_id && i.serie === curtain.serie)
    group = group ? group.group : Math.max(0, ...state.items.map(i => i.group)) + 1
    const numbers = state.items.filter(i => i.group === group).map(i => Number(i.key.split('.')[1]))
    for (let n = 1; n <= quantity; n += 1) {
      const next = Math.max(0, ...numbers) + 1
      numbers.push(next)
      state.items.push({
        key: `C${group}.${next}`,
        group,
        product_id: curtain.product_id,
        productRef: curtain.product_ref,
        serie: curtain.serie,
        ref: curtain.ref,
        control_box: curtain.control_box,
        sin_tarifa: curtain.sin_tarifa,
        price_by: curtain.price_by,
        measure: 'L1',
        l1: null,
        h1: null,
        l2: null,
        h2: null,
        position: null,
        notes: '',
        calc: null,
        calcError: null,
      })
    }
  },
  duplicateItem(state, key) {
    const source = state.items.find(i => i.key === key)
    const next = Math.max(...state.items.filter(i => i.group === source.group).map(i => Number(i.key.split('.')[1]))) + 1
    state.items.push({ ...source, key: `C${source.group}.${next}` })
  },
  removeItem(state, key) {
    state.items = state.items.filter(i => i.key !== key)
    state.extras = state.extras.filter(e => e.curtain !== key)
    state.noBox = state.noBox.filter(k => k !== key)
    state.boxes = state.boxes.map(b => ({ ...b, curtains: b.curtains.filter(c => c !== key) }))
  },
  updateItem(state, { key, changes }) {
    const item = state.items.find(i => i.key === key)
    Object.assign(item, changes)
  },
  addBox(state) {
    state.boxes.push({
      id: Math.max(0, ...state.boxes.map(b => b.id)) + 1, curtains: [], ref: null, variantId: null, resolved: null, error: null,
    })
  },
  removeBox(state, id) {
    state.boxes = state.boxes.filter(b => b.id !== id)
  },
  updateBox(state, { id, changes }) {
    Object.assign(state.boxes.find(b => b.id === id), changes)
  },
  toggleBoxCurtain(state, { id, key }) {
    const selected = state.boxes.find(b => b.id === id).curtains.includes(key)
    state.noBox = state.noBox.filter(k => k !== key)
    // Una cortina solo puede ir en un cuadro
    state.boxes = state.boxes.map(b => ({
      ...b,
      curtains: b.id === id && !selected ? [...b.curtains.filter(c => c !== key), key] : b.curtains.filter(c => c !== key),
    }))
  },
  // Cortinas que el cliente marca expresamente como "Sin cuadro"
  toggleNoBox(state, key) {
    if (state.noBox.includes(key)) {
      state.noBox = state.noBox.filter(k => k !== key)
      return
    }
    state.noBox = [...state.noBox, key]
    state.boxes = state.boxes.map(b => ({ ...b, curtains: b.curtains.filter(c => c !== key) }))
  },
  toggleExtra(state, { productId, key }) {
    const index = state.extras.findIndex(e => e.product_id === productId && e.curtain === key)
    if (index !== -1) {
      state.extras.splice(index, 1)
      pruneDependents(state, key)
      return
    }
    const rules = extraRules(state, productId)
    if (rules.exclusive) {
      state.extras = state.extras.filter(e => e.curtain !== key || extraRules(state, e.product_id).category !== rules.category)
    }
    state.extras.push({
      product_id: productId, curtain: key, ral: '', comments: '',
    })
  },
  updateExtra(state, { productId, key, changes }) {
    Object.assign(state.extras.find(e => e.product_id === productId && e.curtain === key), changes)
  },
}

const actions = {
  async loadCatalog({ commit }) {
    commit('app/loading', true, { root: true })
    try {
      const response = await configuratorApi.getCatalog()
      commit('setCatalog', response.data)
    } catch (e) {
      notifyError(commit, i18n.t('configurator.catalog_error'))
    }
    commit('app/loading', false, { root: true })
  },
  // Devuelve el paso del borrador si había cambios sin guardar de esta oferta
  async loadOffer({ commit }, id) {
    commit('app/loading', true, { root: true })
    let draftStep = null
    try {
      const response = await configuratorApi.getOffer(id)
      commit('resetOffer')
      if (response.data.configurator) commit('restoreOffer', response.data.configurator)
      // Ofertas del tarificador anterior: sin estado del configurador, solo lectura
      commit('setLegacy', !response.data.configurator)
      commit('setOrder', response.data.data)
      const draft = readDraft()
      if (response.data.configurator && draft && Number(draft.orderId) === Number(id)) {
        const { step, ...offer } = draft
        commit('restoreOffer', offer)
        draftStep = step
      }
    } catch (e) {
      notifyError(commit, i18n.t('configurator.get_order_error'))
    }
    commit('app/loading', false, { root: true })
    return draftStep
  },
  async restoreDraft({ commit, dispatch }) {
    const draft = readDraft()
    if (!draft || !draft.items || !draft.items.length) return false
    if (draft.orderId) {
      await dispatch('loadOffer', draft.orderId)
    } else {
      const { step, ...offer } = draft
      commit('resetOffer')
      commit('restoreOffer', offer)
    }
    commit('setStep', draft.step || 1)
    return true
  },
  saveDraft({ state, getters }) {
    const editable = !state.legacy && (!state.order || state.order.status_last.type === 'pending')
    if (!editable || !state.items.length) {
      clearDraft()
      return
    }
    try {
      localStorage.setItem(draftKey(), JSON.stringify(getters.getDraft))
    } catch (e) {
      // sin localStorage (modo privado, cuota llena) la oferta sigue funcionando en memoria
    }
  },
  discardDraft({ commit }) {
    clearDraft()
    commit('resetOffer')
  },
  async calculateItem({ commit, state, getters }, key) {
    const item = state.items.find(i => i.key === key)
    if (item.sin_tarifa || item.price_by) return
    const errors = measureErrors(item)
    if (errors.length || errors.height) {
      commit('updateItem', { key, changes: { calc: null, calcError: null } })
      return
    }
    const useL2 = item.measure === 'L2'
    const l = useL2 ? item.l2 : item.l1
    const h = useL2 ? item.h2 : item.h1
    if (!l || !h) {
      commit('updateItem', { key, changes: { calc: null, calcError: null } })
      return
    }
    const data = new FormData()
    data.append('product_id', item.product_id)
    data.append(useL2 ? 'L2' : 'L1', l)
    data.append(useL2 ? 'H2' : 'H1', h)
    if (item.serie) data.append('serie', item.serie)
    data.append('position', item.position || '')
    // Tarifa 2026 del cliente
    if (getters.getClientId) data.append('client_id', getters.getClientId)
    // Si la medida ha cambiado mientras respondía la API, esta respuesta ya no vale
    const current = () => {
      const now = state.items.find(i => i.key === key)
      return now && now.measure === item.measure && (useL2 ? now.l2 === l && now.h2 === h : now.l1 === l && now.h1 === h)
    }
    try {
      const response = await stepsApi.getCalculator(data)
      if (!current()) return
      const calc = response.data
      commit('updateItem', {
        key,
        // price_pending: la medida es válida pero su tarifa no la cubre; el precio lo pone Tecnitex
        changes: calc.precio || calc.price_pending ? { calc, calcError: null } : { calc: null, calcError: i18n.t('configurator.out_range_error') },
      })
    } catch (e) {
      if (!current()) return
      const error = e.response && e.response.data && e.response.data.error
      commit('updateItem', { key, changes: { calc: null, calcError: apiError(error) } })
    }
  },
  async resolveBox({ commit, state, getters }, id) {
    const box = state.boxes.find(b => b.id === id)
    const items = box.curtains.map(key => state.items.find(i => i.key === key))
    if (!items.length) {
      commit('updateBox', {
        id,
        changes: {
          resolved: null, error: null, ref: null, variantId: null,
        },
      })
      return
    }
    if (items.some(i => !i.calc)) {
      commit('updateBox', {
        id,
        changes: {
          resolved: null, error: i18n.t('configurator.box_needs_measures'), ref: null, variantId: null,
        },
      })
      return
    }
    try {
      const response = await configuratorApi.getControlBox(
        items.map(i => ({ motor: i.calc.motor, num_motor: i.calc.num_motor })),
        items.some(i => getters.requiresSig(i.key)),
      )
      const resolved = response.data
      const ref = resolved.allowed_refs.includes(box.ref) ? box.ref : resolved.allowed_refs[0]
      const option = resolved.options.find(o => o.ref === ref)
      const variantId = option.variants.some(v => v.id === box.variantId) ? box.variantId : option.variants[0].id
      commit('updateBox', {
        id,
        changes: {
          resolved, error: null, ref, variantId,
        },
      })
    } catch (e) {
      const error = e.response && e.response.data && e.response.data.error
      commit('updateBox', {
        id,
        changes: {
          resolved: null, error: error || i18n.t('configurator.box_error'), ref: null, variantId: null,
        },
      })
    }
  },
  async resolveBoxesOf({ dispatch, state }, key) {
    const boxes = state.boxes.filter(b => b.curtains.includes(key))
    await Promise.all(boxes.map(b => dispatch('resolveBox', b.id)))
  },
  async save({ commit, state }) {
    const offer = {
      order_id: state.orderId,
      client_id: currentClientId(),
      curtains: state.items.map(item => ({
        key: item.key,
        product_id: item.product_id,
        serie: item.serie,
        L1: item.measure === 'L1' ? item.l1 : null,
        H1: item.measure === 'L1' ? item.h1 : null,
        L2: item.measure === 'L2' ? item.l2 : null,
        H2: item.measure === 'L2' ? item.h2 : null,
        position: item.position,
        notes: item.notes || null,
      })),
      control_boxes: state.boxes.filter(b => b.variantId && b.curtains.length).map(b => ({
        product_id: b.variantId,
        curtains: b.curtains,
      })),
      extras: state.extras.map(e => ({
        product_id: e.product_id, curtain: e.curtain, ral: e.ral || null, comments: e.comments || null,
      })),
      packaging_type: state.packagingType,
      state: {
        items: state.items,
        boxes: state.boxes,
        noBox: state.noBox,
        extras: state.extras,
        packagingType: state.packagingType,
      },
    }
    commit('app/loading', true, { root: true })
    try {
      const response = await configuratorApi.save(offer)
      commit('setOrder', response.data.data)
      commit('setWarnings', response.data.warnings)
      commit('setErrors', {})
      commit('app/loading', false, { root: true })
      return true
    } catch (e) {
      const data = (e.response && e.response.data) || {}
      commit('setErrors', data.errors || { general: [data.error || i18n.t('configurator.save_error')] })
      commit('setWarnings', data.warnings)
      notifyError(commit, i18n.t('configurator.save_error'))
      commit('app/loading', false, { root: true })
      return false
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
